import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import API, { UPLOADS_BASE_URL } from "../services/api";
import { Link, useNavigate } from "react-router-dom";
import Loader from "../components/Loader";
import {
    FaEdit,
    FaEye,
    FaPlus,
    FaSearch,
    FaTrash,
    FaFileExcel
} from "react-icons/fa";

function Profiles() {
    const navigate = useNavigate();
    const role = localStorage.getItem("role");

    const [profiles, setProfiles] = useState([]);
    const [filteredProfiles, setFilteredProfiles] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const limit = 8;

    // =====================================
    // GET PROFILES
    // =====================================
    const getProfiles = async (currentPage = 1) => {
        try {
            setLoading(true);
            const response = await API.get(
                `/profile/list?page=${currentPage}&limit=${limit}`
            );
            setProfiles(response.data.profiles);
            setFilteredProfiles(response.data.profiles);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getProfiles(page);
    }, [page]);

    // =====================================
    // SEARCH
    // =====================================
    useEffect(() => {
        const filtered = profiles.filter((profile) =>
            profile.personalDetails?.name
                ?.toLowerCase()
                .includes(search.toLowerCase())
        );
        setFilteredProfiles(filtered);
    }, [search, profiles]);

    // =====================================
    // DELETE
    // =====================================
    const deleteProfile = async (id) => {
        const confirmDelete = window.confirm(
            "Are you sure you want to delete this profile?"
        );
        if (!confirmDelete) return;

        try {
            await API.delete(`/profile/delete/${id}`);
            alert("Profile Deleted Successfully");
            getProfiles(page);
        } catch (error) {
            console.log(error);
        }
    };

    // =====================================
    // EXPORT
    // =====================================
    const handleExport = async () => {
        try {
            const response = await API.get("/profile/export", {
                responseType: "blob"
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "entropy_profiles_export.csv");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (error) {
            console.error("Export failed", error);
            alert("Failed to export profiles.");
        }
    };

    return (
        <div className="main-wrapper">
            <Sidebar />

            <div className="content-wrapper">
                {/* HEADER */}
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-4 animate-fade-in">
                    <div>
                        <h2 className="fw-extrabold text-slate mb-1">Profile Management</h2>
                        <p className="text-muted mb-0">Manage and organize all employee profiles</p>
                    </div>

                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-success d-flex align-items-center gap-2 shadow-sm"
                            onClick={handleExport}
                        >
                            <FaFileExcel size={14} /> Export Excel
                        </button>
                        {role !== "viewer" && (
                            <Link
                                to="/add-profile"
                                className="btn btn-primary d-flex align-items-center gap-2 shadow-sm"
                            >
                                <FaPlus size={14} /> Add Profile
                            </Link>
                        )}
                    </div>
                </div>

                {/* SEARCH BAR */}
                <div className="card border-0 shadow-sm mb-4 animate-fade-in" style={{ animationDelay: "0.1s" }}>
                    <div className="card-body p-3">
                        <div className="input-group">
                            <span className="input-group-text bg-transparent border-0 pe-0" style={{ color: "#94a3b8" }}>
                                <FaSearch />
                            </span>
                            <input
                                type="text"
                                placeholder="Search profiles by name..."
                                className="form-control border-0 shadow-none bg-transparent"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <Loader message="Loading profiles..." />
                ) : (
                    <>
                        {/* TABLE CARD */}
                        <div className="card border-0 shadow-sm animate-fade-in" style={{ animationDelay: "0.2s" }}>
                            <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-4" style={{ width: "60px" }}>#</th>
                                        <th style={{ width: "80px" }}>Profile</th>
                                        <th>Name</th>
                                        <th>Department</th>
                                        <th>Mobile</th>
                                        <th>Address</th>
                                        <th>Status</th>
                                        <th className="pe-4 text-end" style={{ width: "160px" }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredProfiles.length > 0 ? (
                                        filteredProfiles.map((profile, index) => (
                                            <tr key={profile._id} className="hover-bg-light">
                                                <td className="ps-4 text-muted font-monospace">
                                                    {((page - 1) * limit) + index + 1}
                                                </td>
                                                <td>
                                                    <img
                                                        src={
                                                            profile.images?.length > 0
                                                                ? `${UPLOADS_BASE_URL}/${profile.images[0]}`
                                                                : "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=100&h=100&q=80"
                                                        }
                                                        alt="profile"
                                                        width="48"
                                                        height="48"
                                                        className="shadow-sm"
                                                        style={{
                                                            borderRadius: "50%",
                                                            objectFit: "cover",
                                                            border: "2px solid #ffffff"
                                                        }}
                                                    />
                                                </td>
                                                <td>
                                                    <div className="fw-bold text-dark">
                                                        {profile.personalDetails?.name || "N/A"}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span 
                                                        className="badge px-3 py-2 text-capitalize"
                                                        style={{
                                                            background: "rgba(79, 70, 229, 0.08)",
                                                            color: "#4f46e5",
                                                            fontWeight: "600"
                                                        }}
                                                    >
                                                        {profile.professionalDetails?.department || "General"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="font-monospace text-muted">
                                                        {profile.personalDetails?.mobile || "N/A"}
                                                    </span>
                                                </td>
                                                <td style={{ maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} className="text-muted">
                                                    {profile.personalDetails?.address || "N/A"}
                                                </td>
                                                <td>
                                                    <span 
                                                        className="badge px-3 py-2"
                                                        style={{
                                                            background: "rgba(16, 185, 129, 0.1)",
                                                            color: "#10b981",
                                                            fontWeight: "600"
                                                        }}
                                                    >
                                                        Active
                                                    </span>
                                                </td>
                                                <td className="pe-4 text-end">
                                                    <div className="d-flex justify-content-end gap-2">
                                                        {/* VIEW */}
                                                        <button
                                                            className="btn btn-sm d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: "32px",
                                                                height: "32px",
                                                                padding: "0",
                                                                borderRadius: "8px",
                                                                background: "rgba(14, 165, 233, 0.1)",
                                                                color: "#0ea5e9",
                                                                border: "none"
                                                            }}
                                                            onClick={() => navigate(`/view-profile/${profile._id}`)}
                                                            title="View Details"
                                                        >
                                                            <FaEye size={14} />
                                                        </button>

                                                        {/* EDIT */}
                                                        {role !== "viewer" && (
                                                            <button
                                                                className="btn btn-sm d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    width: "32px",
                                                                    height: "32px",
                                                                    padding: "0",
                                                                    borderRadius: "8px",
                                                                    background: "rgba(245, 158, 11, 0.1)",
                                                                    color: "#f59e0b",
                                                                    border: "none"
                                                                }}
                                                                onClick={() => navigate(`/edit-profile/${profile._id}`)}
                                                                title="Edit Profile"
                                                            >
                                                                <FaEdit size={14} />
                                                            </button>
                                                        )}

                                                        {/* DELETE */}
                                                        {role === "admin" && (
                                                            <button
                                                                className="btn btn-sm d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    width: "32px",
                                                                    height: "32px",
                                                                    padding: "0",
                                                                    borderRadius: "8px",
                                                                    background: "rgba(239, 68, 68, 0.1)",
                                                                    color: "#ef4444",
                                                                    border: "none"
                                                                }}
                                                                onClick={() => deleteProfile(profile._id)}
                                                                title="Delete Profile"
                                                            >
                                                                <FaTrash size={14} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="text-center py-5 text-muted">
                                                <h5>No Profiles Found</h5>
                                                <p className="small mb-0">Try searching for another name or add a new profile.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* PAGINATION */}
                {totalPages > 1 && (
                    <div className="d-flex justify-content-center align-items-center gap-2 mt-4 animate-fade-in">
                        {[...Array(totalPages)].map((_, index) => (
                            <button
                                key={index}
                                className={`btn d-flex align-items-center justify-content-center font-semibold`}
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    padding: "0",
                                    borderRadius: "50%",
                                    backgroundColor: page === index + 1 ? "var(--primary-color)" : "#ffffff",
                                    color: page === index + 1 ? "#ffffff" : "var(--text-muted)",
                                    border: page === index + 1 ? "none" : "1px solid var(--border-light)",
                                    boxShadow: page === index + 1 ? "0 4px 10px rgba(79, 70, 229, 0.2)" : "none"
                                }}
                                onClick={() => setPage(index + 1)}
                            >
                                {index + 1}
                            </button>
                        ))}
                    </div>
                )}
                    </>
                )}
            </div>
        </div>
    );
}

export default Profiles;